import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    isDevMode,
} from '@angular/core';

import { Oauth2RessourceService } from '@app/oauth2/oauth2-resources.service';

import { Observable, Subject, takeUntil } from 'rxjs';

import { BtlBattlefrontService } from '../../services/btl-battlefront.service';

@Component({
    selector: 'btl-cms-block',
    templateUrl: './cms-block.component.html',
    styleUrls: ['./cms-block.component.scss'],
})
export class CmsBlockComponent implements OnInit, OnDestroy {
    private hasAllParams: boolean = false;
    private _blockId: string;

    public _scavenger = new Subject();
    public hasAlreadyLoad: boolean = false;

    private get blockId(): string {
        return this._blockId;
    }
    @Input() private set blockId(id: string) {
        this._blockId = id;
        if (this.hasAllParams) {
            this.renderBlock();
        }
    }

    @Input() public renderNullBlock: boolean = true;
    @Input() public replaceElement: boolean = false; // TO USE ONLY FOR STATIC BLOCK
    @Input() private extraClass: string;
    @Input() private isBta: boolean;
    @Input() private isCustomUrl: string;
    @Input() private html: string;
    @Input() private battlefront: boolean = true;
    @Input() private replaceContent: { regex: RegExp; replace: string };
    @Input() private allowCustomsActions: boolean;

    @Output() public contentIsLoaded: EventEmitter<boolean> = new EventEmitter<boolean>(true);

    constructor(
        @Inject(DOCUMENT) protected document: Document,
        protected renderer: Renderer2,
        protected element: ElementRef,
        protected http: HttpClient,
        protected oauth2Resource: Oauth2RessourceService,
        protected battlefrontService: BtlBattlefrontService,
    ) {}

    public ngOnInit(): void {
        this.hasAllParams = true;
        this.renderBlock();
    }

    public ngOnDestroy(): void {
        // Used for scavenger unsubscription
        this._scavenger.next(null);
        this._scavenger.complete();
    }

    public renderBlock(): void {
        if (!this.html) {
            let service: Observable<any> = this.battlefrontService.getBtaBlock(this.blockId);

            if (this.isBta) {
                this.battlefront = false;
            }

            if (this.isCustomUrl) {
                service = this.http.get(this.blockId, { responseType: 'text' });
            } else if (this.battlefront) {
                service = this.battlefrontService.getBlock(this.blockId);
            }

            service.pipe(takeUntil(this._scavenger)).subscribe(
                (data: any) => {
                    this.resetBlock();
                    if (this.renderNullBlock || this.blockId) {
                        this.injectHtml(this.isCustomUrl ? data : data.html);
                    }
                },
                () => {
                    this.resetBlock();
                    this.contentIsLoaded.emit(false);
                },
            );
        } else {
            this.injectHtml(this.html);
        }
    }

    protected customActions(domHtml): void {
        // implemented in extended component
    }

    protected injectHtml(html: string): void {
        html = this._canReplaceHtmlContent()
            ? html.replace(this.replaceContent.regex, this.replaceContent.replace)
            : html;
        if (html) {
            if (this._canReplaceHtmlContent()) {
                html = html.replace(this.replaceContent.regex, this.replaceContent.replace);
            }
            const domHtml = this.document.createRange().createContextualFragment(html);
            if (this.allowCustomsActions && !html.includes('disable-custom-actions')) {
                this.customActions(domHtml);
            }

            if (this.hasAlreadyLoad) {
                return;
            }

            if (this.replaceElement) {
                this.element.nativeElement.parentNode.replaceChild(domHtml, this.element.nativeElement);
            } else {
                const container: HTMLElement = this.document.createElement('div');
                // innerHTML native property never execute script, that's why injecting string directly was prefered
                container.classList.add('cms-container');
                if (this.extraClass) {
                    container.classList.add(this.extraClass);
                }
                container.appendChild(domHtml); // String to HTML
                this.renderer.appendChild(this.element.nativeElement, container);
            }
            this.contentIsLoaded.emit(true);
        }
    }

    public resetBlock(): void {
        const childElements = this.element.nativeElement.children;
        for (const child of childElements) {
            this.renderer.removeChild(this.element.nativeElement, child);
        }
    }

    private _canReplaceHtmlContent(): boolean {
        const urlRegExp: RegExp = /http(s?):\/\/web-(.*).(.*)/gim;
        return (isDevMode() || urlRegExp.test(window.location.origin)) && !!this.replaceContent;
    }
}
